import Core from "@/modules/core/service/Core";
import List from "@/modules/dis/service/inspection/private/List";
import Unit from "@/modules/dis/service/inspection/private/Unit";
import Dispatch from "@/modules/dis/service/inspection/private/Dispatch";
import Release from "@/modules/dis/service/inspection/private/Release";
import UnitDataLayout from "@/modules/dis/service/inspection/UnitDataLayout";

const url = {
    byId         : "/api/disposition/order/{0}/inspection/{1}",
    assign       : "/api/disposition/inspection/{1}",
    all          : "/api/disposition/order/inspection",
    byOrder      : "/api/disposition/order/{0}",
    unit: {
        create: "/api/disposition/inspection/{0}?command=unit",
        assign: "/api/disposition/inspection/{0}/unit/{1}",
        reopen: "/api/disposition/inspection/{0}?command=reopen",
        deviceTypeCheck: "/api/disposition/inspection/{0}/device-type/check",
    }
}

const hasElevator = function (inspection) {
    return !!inspection.elevator && inspection.elevator.status;
}

const hastStickers = function (inspection) {
    return inspection.stickers && inspection.stickers.stickers && inspection.stickers.stickers.length > 0;
}

const hasLgSmart = function (inspection) {
    return inspection.scope.lgs && inspection.lgSmart && inspection.lgSmart.assembly && inspection.lgSmart.assembly.assemblyHint && inspection.lgSmart.assembly.assemblyHint.id;
}

const hasConditions = function (inspection) {
    return !!inspection.condition && inspection.condition.status;
}

const hasAudit = function (inspection) {
    return inspection.audit && inspection.audit.entries &&  inspection.audit.entries.length > 0;
}

const hasEsg = function (inspection) {
    return inspection.esg && inspection.esg.entries &&  inspection.esg.entries.length > 0;
}

// Photos can be uploaded to every inspection, so the area is there even while it has none (HCIT-1107)
const hasImages = function () {
    return true;
}

export default {
    areas: {
        UNIT      : "UNIT",
        STICKER   : "STICKER",
        LGS       : "LGS",
        CONDITIONS: "CONDITIONS",
        GBU       : "GBU",
        ESG       : "ESG",
        IMAGES    : "IMAGES"
    },

    list: List,
    unit: Unit,
    dispatch: Dispatch,
    release : Release,

    /**
     * Finds all units and inspection assignments of the given order.
     *
     * @param order the order
     * @return {Promise<*>}
     */
    findByOrder (order) {
        return Core.method.get(url.byOrder, order.id);
    },

    /**
     * Retrieves all inspections that are assigned to orders that have not yet been completed.
     *
     * @return {Promise<*>}
     */
    findAll () {
        return Core.method.get(url.all);
    },

    get (order, unit) {
        return Core.method.get(url.assign, order.id, unit.id);
    },

    getById (order, inspectionId) {
        return Core.method.get(url.byId, order.id, inspectionId);
    },

    store (disposition) {
        return Core.method.put(url.assign, disposition, disposition.order.id, disposition.unitId);
    },

    transfer (inspection, area) {
        return Core.method.putVoid(url.transfer, null, inspection.id, area);
    },

    createUnit (inspection) {
        return Core.method.post(url.unit.create, null, inspection.id);
    },

    assignUnit (inspection, unit) {
        return Core.method.put(url.unit.assign, null, inspection.id, unit.id);
    },

    /**
     * Checks the device type of the inspection against the site type again (HCIT-943), e.g. after the office
     * corrected the site type; returns the inspection with the new conflict state.
     */
    checkDeviceType (inspection) {
        return Core.method.post(url.unit.deviceTypeCheck, null, inspection.id);
    },

    reopen (inspection) {
        return Core.method.post(url.unit.reopen, null, inspection.id);
    },

    toAssignment (inspection) {
        return {
            id             : inspection.id,
            unitId         : inspection.unitId,
            orderId        : inspection.order.id,
            createdBy      : inspection.createdBy,
            createdDate    : inspection.createdDate,
            modifiedBy     : inspection.modifiedBy,
            modifiedDate   : inspection.modifiedDate,
            download       : inspection.download,
            inspectionDate : inspection.inspectionDate,
            technicianId   : inspection.technicianId,
            technicianIdent: inspection.technicianIdent,
            technicianName : inspection.technicianName,
            done           : inspection.done,

            unit: {
                inspectionId: inspection.id,
                id          : inspection.unitId,
                checked     : true,
                zip     : inspection.zip,
                city    : inspection.city,
                country : inspection.country,
                longitude      : inspection.unit.longitude,
                latitude       : inspection.unit.latitude,
                customer: {
                    id  : inspection.order.customer.id,
                    name: inspection.order.customer.name
                },
                status: {
                    id: 1
                },
                street  : inspection.street,
                fabricNo: inspection.elevatorNo,
                elevatorNo: inspection.elevatorNo,
                location: inspection.unit.location,
                order: {
                    id: inspection.order.id,
                    no: inspection.order.no,
                    customer: inspection.order.customer.name,
                    color: inspection.order.color,
                },
            },

            order: {
                id: inspection.order.id,
                no: inspection.order.no,
                customer: inspection.order.customer.name,
                color: inspection.order.color,
            }
        }
    },

    menu (component, inspection) {
        const menu = [];
        menu.push({
            id       : this.areas.UNIT,
            // one name for all device types; the icon follows the reported device type (HCIT-1191)
            name     : component.$t('disposition.unit.menu'),
            icon     : UnitDataLayout.isEscalator (inspection) ? "mdi-escalator" : "mdi-elevator",
            status   : inspection.elevator.status,
            available: hasElevator (inspection),
            data     : inspection.elevator,
            scope    : inspection.scope.elevator
        });

        menu.push({
            id       : this.areas.STICKER,
            name     : component.$t('disposition.sticker.menu'),
            icon     : "mdi-qrcode",
            status   : inspection.stickers.status,
            available: hastStickers (inspection),
            data     : inspection.stickers,
            scope    : inspection.scope.sticker
        });

        menu.push({
            id       : this.areas.LGS,
            name     : component.$t('disposition.lgs.menu'),
            icon     : "mdi-router-wireless",
            status   : inspection.lgSmart.status,
            available: hasLgSmart(inspection),
            data     : inspection.lgSmart,
            scope    : inspection.scope.lgs
        });

        menu.push({
            id       : this.areas.CONDITIONS,
            name     : component.$t('disposition.conditions.menu'),
            icon     : "mdi-elevator",
            status   : inspection.condition.status,
            available: hasConditions(inspection),
            data     : inspection.condition,
            scope    : inspection.scope.condition
        });

        menu.push({
            id       : this.areas.GBU,
            name     : component.$t('disposition.audit.GBU.menu'),
            icon     : "mdi-clipboard-check-outline",
            status   : inspection.audit.status,
            available: hasAudit(inspection),
            data     : inspection.audit,
            scope    : inspection.scope.audit
        });

        menu.push({
            id       : this.areas.ESG,
            name     : component.$t('disposition.audit.ESG.menu'),
            icon     : "mdi-heart",
            status   : inspection.esg.status,
            available: hasEsg(inspection),
            data     : inspection.esg,
            scope    : inspection.scope.esg
        });

                menu.push({
            id       : this.areas.IMAGES,
            name     : component.$t('disposition.images.menu'),
            icon     : "mdi-image-album",
            status   : null,
            available: hasImages(inspection),
            data     : inspection.images,
            scope    : null
        });

        return menu;
    },
}